'use strict'

/**
 * Dropcolor UI Manager
 * --------------------------------------------------------------------
 * PURE UI LAYER (no backend logic, no ColorSample logic).
 *
 * Responsibilities:
 *   ✓ Manage slot DOM structure (empty/full/selected)
 *   ✓ Clone swatch color or texture swatch tiles into slots
 *   ✓ Maintain CSS classes
 *   ✓ Maintain delete button visibility
 *   ✓ Optionally render:
 *       - label inside the color (swatch name)
 *       - info panel (surface name + color name) for the selected slot
 *
 * Not responsible for:
 *   ✗ Creating color objects
 *   ✗ Knowing swatch/texture backend structure
 *   ✗ Updating ColorSample data model
 *
 * All backend → UI sync occurs through CanvasState/ColorSample,
 * which call Dropcolor.replaceDropcolorWithDroppable() and rebuildSlots().
 */
class Dropcolor {
    // ───────────────────────────────────────────────────────────────
    // Config: allows personalizing Dropcolor UI globally
    // ───────────────────────────────────────────────────────────────
    static CONFIG = {
        /**
         * If true, show the color name (swatch label) INSIDE the colored tile
         * appended in displayNewDropcolor().
         */
        showTitleInsideColor: true,

        /**
         * If true, update the right-side info panel with the currently
         * displayed dropcolor: 3 lines (surface, empty, color name).
         */
        showInfoPanel: false,
    }

    /**
     * Public helper to override default UI behavior.
     * Call this once at app startup, e.g.:
     *
     *   Dropcolor.configure({
     *       showTitleInsideColor: false,
     *       showInfoPanel: true,
     *   })
     */
    static configure(options = {}) {
        this.CONFIG = {
            ...this.CONFIG,
            ...options,
        }
    }

    // ───────────────────────────────────────────────────────────────
    // Constants
    // ───────────────────────────────────────────────────────────────
    static SLOT_ID_PREFIX = 'dropcolor' // dropcolor1..5
    static DELETE_BTN_ID_PREFIX = 'deletedropcolor' // deletedropcolor1..5

    static CLASS_DROPCOLOR = 'dropcolor'
    static CLASS_CLICKABLE_TILE = 'clickable-color'
    static CLASS_SWATCHCOLOR_LABEL = 'swatchcolor'
    static CLASS_TEXTURE = 'texture'
    static CLASS_TEXTURE_SWATCH = 'texture-swatch'

    static CLASS_SELECTED = 'dropcolor-selected'
    static CLASS_FULL = 'dropcolor-full'
    static CLASS_EMPTY = 'dropcolor-empty'

    // css classes that identify draggable color/texture tiles
    static DRAGGABLE_CLASSES = ['js-draggable', 'ui-draggable', 'ui-draggable-handle']

    // ───────────────────────────────────────────────────────────────
    // Helpers for ID generation
    // ───────────────────────────────────────────────────────────────

    /**
     * Build a dropcolor slot ID from its numeric index.
     * 1 → "dropcolor1"
     */
    static slotId(n) {
        return `${this.SLOT_ID_PREFIX}${n}`
    }

    /**
     * Build a delete button ID for a slot from its numeric index.
     * 1 → "deletedropcolor1"
     */
    static deleteBtnId(n) {
        return `${this.DELETE_BTN_ID_PREFIX}${n}`
    }

    // ───────────────────────────────────────────────────────────────
    // Clear a single slot (UI only)
    // -----------------------------------------------------------------
    // Removes:
    //    • first child tile
    //    • inline style
    //    • hides delete button
    // Does NOT modify selected/full/empty — caller must rebuildSlots().
    // ───────────────────────────────────────────────────────────────
    static clearSlot(n, root = document) {
        const slot = root.getElementById(this.slotId(n))
        if (!slot) return

        // remove content (we only expect one main child)
        const firstChild = slot.querySelector('div:first-child')
        if (firstChild) firstChild.remove()

        // clear inline style (background, image, etc.)
        slot.removeAttribute('style')

        // hide delete button
        const del = root.getElementById(this.deleteBtnId(n))
        if (del) del.style.display = 'none'

        // optionally clear info panel if all slots are empty
        // (lightweight check: if every dropcolor is empty, wipe info)
        if (this.CONFIG.showInfoPanel) {
            const elements = root.getElementsByClassName(this.CLASS_DROPCOLOR)
            const anyFull = Array.from(elements).some((el) => el.children.length > 0)
            if (!anyFull) {
                this.clearInfoPanel(root)
            }
        }
    }

    // ───────────────────────────────────────────────────────────────
    // Reset multiple slots (default 1..5)
    // Does NOT assign full/empty — caller must call rebuildSlots().
    // ───────────────────────────────────────────────────────────────
    static resetSlots(count = 5, root = document) {
        for (let i = 1; i <= count; i++) {
            this.clearSlot(i, root)
        }
    }

    static replaceDropcolorWithDroppable(id, idClone, root = document) {
        const slot = root.getElementById(id)
        const source = root.getElementById(idClone)

        if (!slot || !source) return

        // Remove existing tile(s)
        while (slot.firstChild) {
            slot.firstChild.remove()
        }

        // Clone draggable swatch color or texture swatch tile
        const clone = source.cloneNode(true)
        const textureSrc = clone.dataset?.textureSrc

        if (textureSrc && !clone.style.backgroundImage) {
            clone.style.backgroundImage = `url('${textureSrc}')`
        }

        // Avoid duplicate IDs for texture-swatch items (source list uses "texture-swatchX")
        // Dropcolor version simply drops that prefix for safety.
        clone.id = clone.id.replace('texture-swatch', '')

        // Prevent click handlers from swatch/texture swatch UI from firing in the dropcolor bar
        clone.classList.remove(this.CLASS_CLICKABLE_TILE)

        // Dropcolor slots own their label policy; do not carry panel tile titles into the slot.
        if (!this.CONFIG.showTitleInsideColor) {
            clone.querySelectorAll(`.${this.CLASS_SWATCHCOLOR_LABEL}`).forEach((node) => node.remove())
        }

        // Ensure draggable classes are present even if swatch/texture swatch rendering stripped them.
        this.DRAGGABLE_CLASSES.forEach((cls) => {
            if (!clone.classList.contains(cls)) {
                clone.classList.add(cls)
            }
        })

        // Append into slot
        slot.appendChild(clone)

        // Recompute dropcolor full/empty UI state
        this.rebuildSlots(root)
    }

    // ───────────────────────────────────────────────────────────────
    // Clear UI selection: remove highlight + hide delete buttons.
    // Does NOT modify full/empty.
    // ───────────────────────────────────────────────────────────────
    static clearSelection(root = document) {
        const elements = root.getElementsByClassName(this.CLASS_DROPCOLOR)

        for (const el of elements) {
            el.classList.remove(this.CLASS_SELECTED)

            const idDigits = (el.id.match(/(\d+)$/) || [])[1] || ''
            const delBtn = idDigits ? root.getElementById(this.deleteBtnId(idDigits)) : null
            if (delBtn) delBtn.style.display = 'none'
        }

        // When nothing is selected, we can optionally blank the info panel
        if (this.CONFIG.showInfoPanel) {
            this.clearInfoPanel(root)
        }
    }

    // ───────────────────────────────────────────────────────────────
    // Select a specific slot (if it has content)
    //
    // Responsibilities:
    //   ✓ Clear previous selection
    //   ✓ Add selected class to target
    //   ✓ Show delete button for target
    //   ✓ Hide delete buttons on others
    //
    // Does NOT change full/empty (that is structural).
    // ───────────────────────────────────────────────────────────────
    static selectSlot(slotIndex, root = document) {
        // Falsy = clear selection
        if (!slotIndex) {
            this.clearSelection(root)
            return
        }

        const targetId = this.slotId(slotIndex)
        const elements = root.getElementsByClassName(this.CLASS_DROPCOLOR)

        this.clearSelection(root)

        for (const el of elements) {
            if (el.id !== targetId) continue

            const hasContent = el.children.length > 0
            el.classList.add(this.CLASS_SELECTED)

            const idDigits = (el.id.match(/(\d+)$/) || [])[1] || ''
            const delBtn = idDigits ? root.getElementById(this.deleteBtnId(idDigits)) : null
            if (delBtn) delBtn.style.display = hasContent ? '' : 'none'

            // NEW: when a slot is selected, show its info (if enabled)
            if (this.CONFIG.showInfoPanel && hasContent) {
                this.updateInfoPanelFromSlot(el, root)
            }

            break
        }
    }

    // ───────────────────────────────────────────────────────────────
    // Recompute empty/full state for ALL dropcolor slots.
    //
    // Responsibilities:
    //   ✓ Remove all UI classes (selected/full/empty)
    //   ✓ Add full class if slot has a tile
    //   ✓ Add empty class if not
    //   ✓ Always hide delete buttons
    //
    // Should be called whenever:
    //   • slot content changes
    //   • slots shift (delete slot, reorder, etc.)
    // ───────────────────────────────────────────────────────────────
    static rebuildSlots(root = document) {
        const elements = root.getElementsByClassName(this.CLASS_DROPCOLOR)

        for (const el of elements) {
            const hasContent = el.children.length > 0

            const idDigits = (el.id.match(/(\d+)$/) || [])[1] || ''
            const delBtn = idDigits ? root.getElementById(this.deleteBtnId(idDigits)) : null

            // Reset state
            el.classList.remove(this.CLASS_SELECTED, this.CLASS_FULL, this.CLASS_EMPTY)

            // Full or empty?
            el.classList.add(hasContent ? this.CLASS_FULL : this.CLASS_EMPTY)

            // Delete buttons hidden during rebuild
            if (delBtn) delBtn.style.display = 'none'
        }
    }

    // ───────────────────────────────────────────────────────────────
    // Extract background-image URL from a draggable helper
    // (used by texture & texture-swatch logic)
    // ───────────────────────────────────────────────────────────────
    static getBackgroundImageURL(ui) {
        const el = ui.helper[0]
        const backgroundImage = el.style.backgroundImage

        if (backgroundImage) {
            return backgroundImage.replace('url(', '').replace(')', '').replaceAll('"', '')
        }

        return el.dataset?.textureSrc || ''
    }

    // ───────────────────────────────────────────────────────────────
    // Extract colorID attribute (legacy helper)
    // ───────────────────────────────────────────────────────────────
    static getColorID(id) {
        return document.getElementById(id)?.getAttribute('colorid')
    }

    // ───────────────────────────────────────────────────────────────
    // Right-side info panel helpers
    // But now: one inline panel per slot, next to each dropcolor.
    // In Blade, each slot column will contain:
    //
    //   <div class="dropcolor-inline-wrapper">
    //       <div id="dropcolor1" class="dropcolor ..."></div>
    //       <div class="dropcolor-inline-info" data-dropcolor-index="1">
    //           <div class="dropcolor-info-line dropcolor-info-surface" data-line="surface"></div>
    //           <div class="dropcolor-info-line dropcolor-info-empty" data-line="empty">&nbsp;</div>
    //           <div class="dropcolor-info-line dropcolor-info-color" data-line="color"></div>
    //       </div>
    //   </div>
    //
    // JS will show only the .dropcolor-inline-info that matches the
    // current dropcolor_id.
    // ───────────────────────────────────────────────────────────────

    /**
     * Clear all inline info panels for all slots.
     */
    static clearInfoPanel(root = document) {
        // Find every inline info block for dropcolors
        const infoBlocks = root.querySelectorAll('.dropcolor-inline-info')

        infoBlocks.forEach((block) => {
            block.classList.remove('is-visible')

            const lines = block.querySelectorAll('.dropcolor-info-line')
            lines.forEach((line) => {
                const textSpan = line.querySelector('[data-line-color-text]')
                if (textSpan) {
                    textSpan.textContent = ''
                } else {
                    line.textContent = ''
                }
            })
        })
    }

    /**
     * Update the inline info panel next to the given colorObject.dropcolor_id.
     *
     * When we have the full colorObject (called from displayNewDropcolor),
     * we also store the surface + color name on the slot as data attributes
     * so that later, when the user just "selects" a slot, we can rebuild
     * the panel from the DOM alone.
     */
    static updateInfoPanel(colorObject, root = document) {
        if (!colorObject || !colorObject.dropcolor_id) return

        const slot = root.getElementById(this.slotId(colorObject.dropcolor_id))
        if (!slot) return

        // Surface name (first line)
        const surfaceName = colorObject.name

        // Color name (third line)
        const colorName = colorObject?.swatch_color?.name ?? ''

        // Persist the data on the slot so we can use it when the user
        // simply selects the slot later.
        slot.dataset.surfaceName = surfaceName
        slot.dataset.colorName = colorName

        // Now update the inline info block using the slot element
        this.updateInfoPanelFromSlot(slot, root)
    }

    /**
     * Update the inline info panel based on a slot DOM element.
     *
     * This is used:
     *   - after displayNewDropcolor() (via updateInfoPanel)
     *   - when a user selects a slot (via selectSlot)
     *
     * It reads slot.dataset.surfaceName / slot.dataset.colorName if present.
     */
    static updateInfoPanelFromSlot(slotEl, root = document) {
        if (!slotEl) return

        // Determine which slot index this is (1..5) from its id (e.g. "dropcolor3")
        const match = slotEl.id.match(/(\d+)$/)
        const index = match ? match[1] : null
        if (!index) return

        // Find the inline info block for this specific slot
        const infoBlock = root.querySelector(`.dropcolor-inline-info[data-dropcolor-index="${index}"]`)
        if (!infoBlock) return

        // Read previously stored data (from updateInfoPanel(colorObject))
        const surfaceName = slotEl.dataset.surfaceName || ''
        const colorName = slotEl.dataset.colorName || ''

        // Clear everything first
        this.clearInfoPanel(root)

        const surfaceLine = infoBlock.querySelector('[data-line="surface"]')
        const emptyLine = infoBlock.querySelector('[data-line="empty"]')
        const colorLine = infoBlock.querySelector('[data-line="color"]')
        const colorText = colorLine?.querySelector('[data-line-color-text]')

        if (surfaceLine) surfaceLine.textContent = surfaceName
        if (emptyLine) emptyLine.textContent = '' // second line stays empty
        if (colorText) {
            colorText.textContent = colorName
        } else if (colorLine) {
            colorLine.textContent = colorName
        }

        infoBlock.classList.add('is-visible')
    }

    // ───────────────────────────────────────────────────────────────
    // Generic renderer for a dropcolor tile given a color model.
    //
    // This is used when backend logic creates a "colorObject"
    // with fields:
    //   • dropcolor_id   → index 1..5
    //   • type           → 0: plain color, 1: texture, 2: texture-swatch
    //   • swatch_color   → { name, sr, sg, sb, color_filename, ... }
    //   • swatch         → (for type 1)
    //   • texture        → (for type 2)
    //   • texture_swatch → (for type 2)
    //
    // This method:
    //   ✓ Creates a tile <div>
    //   ✓ Applies draggable classes (for DnD)
    //   ✓ Optionally adds the label inside the color (CONFIG.showTitleInsideColor)
    //   ✓ Computes background color / image
    //   ✓ Inserts tile into the correct dropcolor slot
    //   ✓ Optionally updates the right-side info panel
    // ───────────────────────────────────────────────────────────────
    static displayNewDropcolor(colorObject, root = document) {
        if (!colorObject || !colorObject.dropcolor_id) return

        const el = root.createElement ? root.createElement('div') : document.createElement('div')
        this.DRAGGABLE_CLASSES.forEach((c) => el.classList.add(c))

        if (this.CONFIG.showTitleInsideColor) {
            const label = document.createElement('div')
            label.className = this.CLASS_SWATCHCOLOR_LABEL
            label.textContent = colorObject?.swatch_color?.name ?? ''
            el.appendChild(label)
        }

        if (colorObject.type === 2) {
            el.classList.add(this.CLASS_TEXTURE, this.CLASS_TEXTURE_SWATCH)
            const imagePath = `/texture-swatches/${colorObject.texture_swatch.id}/image/${colorObject.texture.image_filename}`
            el.style.backgroundImage = `url(${imagePath})`
            if (colorObject.swatch_color) {
                const { sr, sg, sb } = colorObject.swatch_color
                el.style.backgroundColor = `rgb(${sr}, ${sg}, ${sb})`
            }
        } else if (colorObject.type === 1) {
            el.classList.add(this.CLASS_TEXTURE)
            const imagePath = `/swatches/${colorObject.swatch.id}/color/${colorObject.swatch_color.color_filename}`
            el.style.backgroundImage = `url(${imagePath})`
        } else {
            const { sr, sg, sb } = colorObject.swatch_color
            el.style.backgroundColor = `rgb(${sr}, ${sg}, ${sb})`
        }

        const slot = root.getElementById(this.slotId(colorObject.dropcolor_id))
        if (!slot) return
        if (slot.firstChild) slot.removeChild(slot.firstChild)
        slot.appendChild(el)

        if (this.CONFIG.showInfoPanel) {
            this.updateInfoPanel(colorObject, root)
        }
    }
}

export default Dropcolor
